/**
 * rest.ts — Categoría "Almuerzo" (11 productos combinados con recetas reales).
 * Todos los ingredientes referencian productos existentes en otras categorías.
 */
import { buildProduct } from "./_factory";
import { REST_MODIFIERS_BY_INDEX } from "./_modifiersRest";
import {
  HORARIO_ALMUERZO,
  withDemoCajaLibreIfModifiers,
} from "./_restaurantDemo.constants";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_rest",
  skuPrefix: "REST",
  categoria: "Almuerzo",
} as const;

const lunch = (
  input: Omit<Parameters<typeof buildProduct>[0], keyof typeof CAT>,
) =>
  buildProduct({
    ...CAT,
    disponibilidadHoraria: HORARIO_ALMUERZO,
    ...withDemoCajaLibreIfModifiers(input),
  });

export const cat_rest_products: Producto[] = [
  lunch({
    index: 0,
    nombre: "Bandeja Paisa",
    marca: "Casa",
    costo: 0,
    precio: 32500,
    stock: 25,
    stockMinimo: 8,
    stockMaximo: 50,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "20 min",
    descripcionLarga:
      "Plato tradicional paisa: arroz blanco, frijoles rojos guisados, carne molida, chicharrón, chorizo, huevo frito, plátano maduro, aguacate y arepa. La historia en un plato.",
    especificaciones: [
      { clave: "Tipo", valor: "Plato típico" },
      { clave: "Tiempo prep", valor: "20 min" },
    ],
    imageKeywords: ["bandeja-paisa", "colombian-dish", "traditional-food"],
    color: "bg-amber-700",
    shape: "square",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Fríjol Rojo 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 4200,
        cantidadUso: 100,
        unidadUso: "g",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 100,
        unidadUso: "g",
      },
      {
        nombre: "Chorizo Argentino x5",
        cantidadCompra: 5,
        unidadCompra: "unidad",
        costoCompra: 12800,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Tocineta Ahumada 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 9800,
        cantidadUso: 30,
        unidadUso: "g",
      },
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Plátano Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1200,
        cantidadUso: 0.5,
        unidadUso: "unidad",
      },
      {
        nombre: "Aguacate Hass",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 3500,
        cantidadUso: 0.5,
        unidadUso: "unidad",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[0],
  }),
  lunch({
    index: 1,
    nombre: "Arroz con Pollo",
    marca: "Casa",
    costo: 0,
    precio: 14000,
    stock: 30,
    stockMinimo: 10,
    stockMaximo: 60,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Arroz amarillo cocinado con pechuga de pollo desmechada, zanahoria, cebolla y especias. Plato nutritivo y abundante.\n\nPrecio base $14.000 en el local. Para llevar +$1.000 (icopor). Elige sopa, bebida, cubiertos y acompañamientos.",
    especificaciones: [
      { clave: "Porciones", valor: "1 adulta" },
      { clave: "Tiempo prep", valor: "15 min" },
    ],
    imageKeywords: ["chicken-rice", "arroz-pollo", "yellow-rice"],
    color: "bg-yellow-500",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 180,
        unidadUso: "g",
      },
      {
        nombre: "Zanahoria 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 1900,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 30,
        unidadUso: "g",
      },
      {
        nombre: "Aceite de Girasol Girasoli 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 9800,
        cantidadUso: 20,
        unidadUso: "ml",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[1],
  }),
  lunch({
    index: 2,
    nombre: "Sancocho de Gallina",
    marca: "Casa",
    costo: 0,
    precio: 22500,
    stock: 22,
    stockMinimo: 6,
    stockMaximo: 45,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "25 min",
    descripcionLarga:
      "Sopa tradicional con presa de gallina criolla, plátano verde, papa, zanahoria, cilantro y especias. Rinde el alma, calienta el cuerpo.",
    especificaciones: [
      { clave: "Porciones", valor: "1 abundante" },
      { clave: "Tiempo prep", valor: "25 min" },
    ],
    imageKeywords: ["chicken-soup", "sancocho", "soup"],
    color: "bg-amber-600",
    shape: "circle",
    gastoMargen: "estandar",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 200,
        unidadUso: "g",
      },
      {
        nombre: "Plátano Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1200,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Zanahoria 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 1900,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 40,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[2],
  }),
  lunch({
    index: 3,
    nombre: "Filete de Tilapia Frito",
    marca: "Casa",
    costo: 0,
    precio: 24900,
    stock: 25,
    stockMinimo: 8,
    stockMaximo: 50,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "12 min",
    descripcionLarga:
      "Filete de tilapia fresca apanado y frito al punto. Servido con arroz, plátano patacón, ensalada fresca y limón. Clásico costeño.",
    especificaciones: [
      { clave: "Pescado", valor: "Tilapia 250g" },
      { clave: "Tiempo prep", valor: "12 min" },
    ],
    imageKeywords: ["fried-fish", "pescado-frito", "fish-plate"],
    color: "bg-sky-600",
    shape: "square",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Filete de Tilapia 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 250,
        unidadUso: "g",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Plátano Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1200,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Harina de Trigo Haz de Oros 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 4500,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Aceite de Girasol Girasoli 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 9800,
        cantidadUso: 40,
        unidadUso: "ml",
      },
      {
        nombre: "Limón Tahití 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 50,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[3],
  }),
  lunch({
    index: 4,
    nombre: "Lomo al Plato",
    marca: "Casa",
    costo: 0,
    precio: 36900,
    stock: 20,
    stockMinimo: 6,
    stockMaximo: 40,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Solomillo de res al punto que té elijas, cebolla caramelizada y papas salteadas. Servido en plato caliente con pan de acompañamiento.",
    especificaciones: [
      { clave: "Corte", valor: "Solomillo 250g" },
      { clave: "Término", valor: "A gusto" },
    ],
    imageKeywords: ["beef-steak-plate", "solomillo", "steak"],
    color: "bg-red-900",
    shape: "square",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Solomillo de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 48500,
        cantidadUso: 250,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 200,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 60,
        unidadUso: "g",
      },
      {
        nombre: "Mantequilla Alpina 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 7200,
        cantidadUso: 15,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[4],
  }),
  lunch({
    index: 5,
    nombre: "Ajiaco Santafereño",
    marca: "Casa",
    costo: 0,
    precio: 24500,
    stock: 25,
    stockMinimo: 8,
    stockMaximo: 50,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "20 min",
    descripcionLarga:
      "Sopa bogotana con tres tipos de papa, pollo desmechado, mazorca, guasca, crema de leche y aguacate. Acompañada de arroz blanco.",
    especificaciones: [
      { clave: "Porciones", valor: "1 abundante" },
      { clave: "Tiempo prep", valor: "20 min" },
    ],
    imageKeywords: ["ajiaco", "colombian-soup", "chicken-stew"],
    color: "bg-amber-300",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 180,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 300,
        unidadUso: "g",
      },
      {
        nombre: "Crema de Leche Alpina 200ml",
        cantidadCompra: 200,
        unidadCompra: "ml",
        costoCompra: 3800,
        cantidadUso: 40,
        unidadUso: "ml",
      },
      {
        nombre: "Aguacate Hass",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 3500,
        cantidadUso: 0.5,
        unidadUso: "unidad",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 80,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[5],
  }),
  lunch({
    index: 6,
    nombre: "Pasta Boloñesa",
    marca: "Casa",
    costo: 0,
    precio: 19900,
    stock: 30,
    stockMinimo: 10,
    stockMaximo: 60,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "12 min",
    descripcionLarga:
      "Espagueti al dente con salsa boloñesa casera (carne molida, tomate, cebolla), gratinada con mozzarella. Servida con pan al ajo.",
    especificaciones: [
      { clave: "Tipo pasta", valor: "Espagueti" },
      { clave: "Tiempo prep", valor: "12 min" },
    ],
    imageKeywords: ["bolognese", "pasta-carne", "spaghetti-meat"],
    color: "bg-red-700",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pasta Espagueti Doria 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2800,
        cantidadUso: 100,
        unidadUso: "g",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Tomate Chonto 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2200,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Queso Mozzarella Colanta 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 11000,
        cantidadUso: 50,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[6],
  }),
  lunch({
    index: 7,
    nombre: "Ensalada César con Pollo",
    marca: "Casa",
    costo: 0,
    precio: 17500,
    stock: 35,
    stockMinimo: 10,
    stockMaximo: 70,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "8 min",
    descripcionLarga:
      "Lechuga crespa, pollo a la plancha en tiras, queso parmesano, crutones de pan tostado y aderezo césar cremoso de la casa.",
    especificaciones: [
      { clave: "Base", valor: "Lechuga + pollo" },
      { clave: "Tiempo prep", valor: "8 min" },
    ],
    imageKeywords: ["caesar-salad", "chicken-salad", "healthy-plate"],
    color: "bg-green-600",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Lechuga Crespa Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1800,
        cantidadUso: 0.6,
        unidadUso: "unidad",
      },
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 140,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Pan Blanco Tajado Bimbo 680g",
        cantidadCompra: 22,
        unidadCompra: "unidad",
        costoCompra: 6500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Mayonesa Fruco 380g",
        cantidadCompra: 380,
        unidadCompra: "g",
        costoCompra: 7200,
        cantidadUso: 20,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[7],
  }),
  lunch({
    index: 8,
    nombre: "Costillas BBQ 500g",
    marca: "Casa",
    costo: 0,
    precio: 38500,
    stock: 18,
    stockMinimo: 5,
    stockMaximo: 35,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "25 min",
    descripcionLarga:
      "Costilla de cerdo cocinada 3 horas al horno con salsa BBQ ahumada, servida con papas rústicas y ensalada de repollo.",
    especificaciones: [
      { clave: "Porción", valor: "500g costilla" },
      { clave: "Cocción", valor: "Lenta 3h" },
    ],
    imageKeywords: ["bbq-ribs", "pork-ribs", "barbecue-plate"],
    color: "bg-red-900",
    shape: "square",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Costilla de Cerdo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 18200,
        cantidadUso: 500,
        unidadUso: "g",
      },
      {
        nombre: "Salsa BBQ Heinz 400g",
        cantidadCompra: 400,
        unidadCompra: "g",
        costoCompra: 9200,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 200,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[8],
  }),
  lunch({
    index: 9,
    nombre: "Salmón al Grill",
    marca: "Casa",
    costo: 0,
    precio: 44900,
    stock: 18,
    stockMinimo: 5,
    stockMaximo: 35,
    unidad: "plato",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Filete de salmón atlántico al grill con mantequilla de limón y hierbas. Acompañado de puré de papa y verduras salteadas.",
    especificaciones: [
      { clave: "Pescado", valor: "Salmón 200g" },
      { clave: "Tiempo prep", valor: "15 min" },
    ],
    imageKeywords: ["grilled-salmon", "salmon-plate", "healthy-fish"],
    color: "bg-pink-500",
    shape: "diamond",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Salmón Fresco 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 38500,
        cantidadUso: 200,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 250,
        unidadUso: "g",
      },
      {
        nombre: "Zanahoria 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 1900,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Mantequilla Alpina 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 7200,
        cantidadUso: 20,
        unidadUso: "g",
      },
      {
        nombre: "Limón Tahití 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 40,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[9],
  }),
  lunch({
    index: 10,
    nombre: "Almuerzo",
    marca: "Casa",
    costo: 0,
    precio: 14000,
    stock: 40,
    stockMinimo: 12,
    stockMaximo: 80,
    unidad: "plato",
    ubicacionFisica: "Cocina, menú del día",
    proveedor: "Producción propia",
    tiempoEntrega: "15 min",
    descripcionLarga:
      "Menú del día personalizable: precio base $14.000 con sopa. Elige dónde lo consumes (local o para llevar + icopor), sopa, principio, proteína, bebida y acompañamientos.\n\nSin sopa -$2.000 · Para llevar +$1.000 (icopor). Proteínas premium (pechuga gratinada, mojarra) valen igual con o sin sopa.",
    especificaciones: [
      { clave: "Tipo", valor: "Menú del día" },
      { clave: "Precio base", valor: "$14.000 (con sopa)" },
      { clave: "Tiempo prep", valor: "15 min" },
    ],
    imageKeywords: ["colombian-lunch", "executive-lunch", "daily-menu"],
    color: "bg-orange-500",
    shape: "circle",
    gastoMargen: "estandar",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Fríjol Rojo 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 4200,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Plátano Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1200,
        cantidadUso: 0.5,
        unidadUso: "unidad",
      },
      {
        nombre: "Limón Tahití 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 3200,
        cantidadUso: 30,
        unidadUso: "g",
      },
    ],
    gruposModificadores: REST_MODIFIERS_BY_INDEX[10],
  }),
];
